import { extent } from 'd3-array';
import { scaleBand, scaleLinear, type ScaleLinear } from 'd3-scale';

import { accentAt } from './palette';
import { buildTimeTicks, isoToEpochMs } from './time-ticks';
import type { ChartMargin, ChartSeries, NumericSeries, XScaleKind, YAxisSide } from './types';

/** Default drawing insets leaving room for axis labels. */
export const DEFAULT_MARGIN: ChartMargin = { top: 8, right: 8, bottom: 24, left: 56 };

/** One x-axis tick — the raw value plus its px position and display label. */
export interface XTick {
  value: string | number;
  position: number;
  label: string;
}

/**
 * Scale-kind-agnostic x-scale. Primitives only ever need a datum's center
 * position, the slot width for bars, and the tick list.
 */
export interface XScale {
  kind: XScaleKind;
  /** Ordered unique x-values present in the data */
  domain: (string | number)[];
  /** Center x-position of a datum in px */
  position: (x: string | number) => number;
  /** Width one datum may occupy (bar group width) */
  bandwidth: number;
  /** Distance between adjacent datum centers */
  step: number;
  /** Axis ticks with px positions */
  ticks: (count?: number) => XTick[];
}

export interface BuildXScaleOptions {
  kind: XScaleKind;
  domain: (string | number)[];
  width: number;
  /** Band padding (0–1), band scale only */
  bandPadding?: number;
}

/** Builds the x-scale for the requested kind over the data domain. */
export function buildXScale({
  kind,
  domain,
  width,
  bandPadding = 0.2
}: BuildXScaleOptions): XScale {
  const safeWidth = Math.max(width, 0);
  if (kind === 'band') {
    const scale = scaleBand<string>()
      .domain(domain.map(String))
      .range([0, safeWidth])
      .padding(bandPadding);
    const bandwidth = scale.bandwidth();
    return {
      kind,
      domain,
      position: x => (scale(String(x)) ?? 0) + bandwidth / 2,
      bandwidth,
      step: scale.step(),
      ticks: () =>
        domain.map(value => ({
          value,
          position: (scale(String(value)) ?? 0) + bandwidth / 2,
          label: String(value)
        }))
    };
  }

  const toNumber = kind === 'time' ? isoToEpochMs : (x: string | number) => Number(x);
  const numericDomain = domain.map(toNumber);
  const [min = 0, max = 0] = extent(numericDomain);
  const scale = scaleLinear()
    .domain(min === max ? [min - 1, max + 1] : [min, max])
    .range([0, safeWidth]);
  if (kind === 'linear') scale.nice(5);

  // Slot width from the tightest gap between adjacent points, so bars on
  // continuous scales never overlap; a lone point gets the whole width.
  const sorted = [...numericDomain].sort((a, b) => a - b);
  let minGapPx = safeWidth;
  for (let i = 1; i < sorted.length; i++) {
    // Zero gaps are skipped: two domain entries can share a coordinate (e.g. the same
    // day as a date and as a timestamp), and letting that win would collapse the slot
    // width to 0 — invisible bars and dead hover bands rather than a degraded chart.
    const gap = Math.abs(scale(sorted[i]) - scale(sorted[i - 1]));
    if (gap > 0) minGapPx = Math.min(minGapPx, gap);
  }
  const step = sorted.length > 1 && minGapPx > 0 ? minGapPx : safeWidth;
  const bandwidth = step * 0.8;

  return {
    kind,
    domain,
    position: x => scale(toNumber(x)),
    bandwidth,
    step,
    ticks: (count = 5) => {
      if (kind === 'time') {
        const [lo, hi] = scale.domain();
        return buildTimeTicks(lo, hi, count).map(tick => ({
          value: tick.iso,
          position: scale(tick.epochMs),
          label: tick.iso
        }));
      }
      return scale
        .ticks(count)
        .map(value => ({ value, position: scale(value), label: String(value) }));
    }
  };
}

/** Linear y-scale over the value range, anchored at zero and niced. */
export function buildLinearScale(values: number[], height: number): ScaleLinear<number, number> {
  const [min = 0, max = 0] = extent(values);
  const low = Math.min(min, 0);
  const high = Math.max(max, 0);
  return scaleLinear()
    .domain(low === high ? [low, low + 1] : [low, high])
    .nice(5)
    .range([Math.max(height, 0), 0]);
}

/** Evenly spaced, human-readable ticks across a scale domain. */
export function buildTicks(scale: ScaleLinear<number, number>, count = 5): number[] {
  return scale.ticks(count);
}

/**
 * Unique x-values across all series, ordered for the axis. Band categories sort
 * lexicographically: the period buckets the producer emits are zero-padded ISO
 * ("2012-01", "2012-Q1"), so this reads chronologically and stays correct when two
 * series cover disjoint buckets. The XLSX export mirrors this order — a file must
 * list its rows the way the chart it came from reads.
 */
export function collectDomain(
  series: ChartSeries[],
  kind: XScaleKind = 'band'
): (string | number)[] {
  const seen = new Set<string | number>();
  for (const s of series) for (const point of s.points) seen.add(point.x);
  const domain = [...seen];
  if (kind === 'band') return domain.sort((a, b) => String(a).localeCompare(String(b)));
  const toNumber = kind === 'time' ? isoToEpochMs : (x: string | number) => Number(x);
  return domain.sort((a, b) => toNumber(a) - toNumber(b));
}

/** Resolves series into per-domain numeric values with a palette color. */
export function toNumericSeries(
  series: ChartSeries[],
  domain: (string | number)[]
): NumericSeries[] {
  return series.map((s, index) => {
    const byX = new Map(s.points.map(point => [point.x, point.y]));
    return {
      label: s.label,
      color: accentAt(index),
      currency: s.currency,
      axis: s.axis ?? 'left',
      values: domain.map(x => byX.get(x))
    };
  });
}

/** Every defined numeric value across the resolved series, per y-axis side. */
export function collectValues(series: NumericSeries[], axis: YAxisSide = 'left'): number[] {
  return series
    .filter(s => s.axis === axis)
    .flatMap(s => s.values.filter((v): v is number => v !== undefined));
}

/**
 * Cumulative stack offsets per datum — `values[i]` becomes the segment top and
 * `bases[i]` the segment bottom. Negative values stack downward separately.
 */
export function stackSeries(
  series: NumericSeries[]
): { series: NumericSeries; bases: (number | undefined)[]; tops: (number | undefined)[] }[] {
  const domainLength = series[0]?.values.length ?? 0;
  const positive = new Array<number>(domainLength).fill(0);
  const negative = new Array<number>(domainLength).fill(0);

  return series.map(s => {
    const bases: (number | undefined)[] = [];
    const tops: (number | undefined)[] = [];
    s.values.forEach((value, i) => {
      if (value === undefined) {
        bases.push(undefined);
        tops.push(undefined);
        return;
      }
      const running = value >= 0 ? positive : negative;
      bases.push(running[i]);
      running[i] += value;
      tops.push(running[i]);
    });
    return { series: s, bases, tops };
  });
}

/** Y-extent of a stacked chart — the summed tops per datum plus zero. */
export function collectStackedValues(series: NumericSeries[], axis: YAxisSide = 'left'): number[] {
  const stacked = stackSeries(series.filter(s => s.axis === axis));
  const values: number[] = [0];
  for (const layer of stacked) {
    for (const top of layer.tops) if (top !== undefined) values.push(top);
  }
  return values;
}
